import { ApiError } from './apiError';
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from './authToken';

// Phase 2 auth-hardening fetch wrapper. Every service in the app should go
// through this so:
//   - the in-memory access token is automatically attached to Authorization,
//   - a 401 transparently triggers a single-flight /auth/refresh + retry,
//   - the refresh cookie is sent (credentials: 'include') and stored by the
//     browser without any JS interaction (it's httpOnly).
//
// The single-flight pattern matters: if 6 service calls all 401 at once
// (e.g. on tab wake after the access token expired), we should fire ONE
// refresh and have all six retries piggyback on its result — not 6 refreshes
// that race each other and trigger the rotation-reuse detector server-side.

const API_BASE =
  import.meta.env.VITE_API_URL || 'http://localhost:4000';

let inFlightRefresh: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const r = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) {
        // 429 (rate-limited) and 5xx (server blip) are transient — the refresh
        // cookie may be perfectly valid, so don't tear the session down.
        // Leave the in-memory token in place and let the caller surface the
        // original 401; a later attempt can recover once the limit/outage clears.
        if (r.status === 429 || r.status >= 500) return null;
        clearAccessToken();
        return null;
      }
      const body = (await r.json()) as { accessToken?: string };
      if (!body.accessToken) {
        clearAccessToken();
        return null;
      }
      setAccessToken(body.accessToken);
      return body.accessToken;
    } catch {
      // A network exception is just as transient as a 429/5xx — the refresh
      // cookie may still be valid. Keep the in-memory token rather than tearing
      // the session down; a later attempt recovers once connectivity returns.
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  // Accept any object literal as a JSON body — DTO interfaces from
  // @printtat/shared don't carry index signatures, so requiring
  // Record<string, unknown> here would force every caller to cast.
  body?: BodyInit | null | object;
  // Skip the Authorization header even when an access token is present.
  // Useful for public endpoints (sign-in/sign-up) where attaching a stale
  // token would just cause a confusing 401-and-retry cycle.
  skipAuth?: boolean;
  // If true, a 401 will NOT trigger a refresh attempt. The auth endpoints
  // themselves use this so /auth/refresh can't recursively call itself.
  skipRefreshOn401?: boolean;
}

function buildInit(token: string | null, options: ApiFetchOptions): RequestInit {
  const headers = new Headers(options.headers ?? {});
  const isFormData = options.body instanceof FormData;
  if (!isFormData && options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token && !options.skipAuth && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let body: BodyInit | null | undefined;
  if (options.body == null) {
    body = options.body as null | undefined;
  } else if (
    typeof options.body === 'string' ||
    options.body instanceof FormData ||
    options.body instanceof Blob ||
    options.body instanceof URLSearchParams ||
    options.body instanceof ArrayBuffer
  ) {
    body = options.body as BodyInit;
  } else {
    body = JSON.stringify(options.body);
  }

  return {
    ...options,
    headers,
    body,
    // Send the refresh cookie alongside cross-origin requests. CORS on the
    // backend already has credentials: true.
    credentials: options.credentials ?? 'include',
  };
}

export async function apiFetch(
  path: string,
  options: ApiFetchOptions = {},
): Promise<Response> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  const firstAttempt = await fetch(
    url,
    buildInit(getAccessToken(), options),
  );
  if (firstAttempt.status !== 401 || options.skipRefreshOn401) {
    return firstAttempt;
  }

  // Burn the body so the underlying TCP slot can be reused; we don't care
  // about it (the retry produces the real response).
  firstAttempt.body?.cancel?.();

  const refreshed = await refreshAccessToken();
  if (!refreshed) {
    return firstAttempt;
  }
  return fetch(url, buildInit(refreshed, options));
}

// Helper that throws an ApiError for non-2xx responses with a JSON body
// containing { message, code }. Services should use this when they don't
// need the raw Response.
export async function apiJson<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const res = await apiFetch(path, options);
  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    let code: string | undefined;
    try {
      const body = (await res.json()) as { message?: string; code?: string };
      if (body.message) message = body.message;
      if (body.code) code = body.code;
    } catch {
      // Body wasn't JSON; keep the default message.
    }
    throw new ApiError(message, res.status, code);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
